<!-- BaseSearchSelect.vue (범용 검색 셀렉트. focus 시 드롭다운 오픈, 다중 선택 + 칩) -->
<template>
  <div class="lms_search_select">
    <div
      class="lms_search_input_wrap"
      :class="{ open: isOpen, disabled: disabled, error: hasError }"
      @click="onWrapClick"
    >
      <i class="mdi mdi-magnify lms_search_icon"></i>
      <!-- 단일 선택 모드: 드롭다운 닫혀있을 때 선택값을 박스 안에 표시 -->
      <span
        v-if="single && selectedLabel && !isOpen"
        class="lms_search_single_value"
      >{{ selectedLabel }}</span>
      <input
        ref="inputEl"
        v-show="isOpen || !(single && selectedLabel)"
        v-model="keyword"
        type="text"
        class="lms_search_input"
        :placeholder="placeholder"
        :disabled="disabled"
        @focus="openDropdown"
        @keydown.esc.prevent="closeDropdown"
        @keydown.enter.prevent="onEnter"
      />
    </div>

    <Teleport to="body">
      <div v-if="isOpen" ref="dropdownRef" class="lms_search_dropdown" :style="dropdownStyle">
        <!-- 로딩 중에는 "표시할 항목이 없습니다" 대신 인디케이터 — 목록이 늦게 오는 동안 빈 상태로 보이던 문제 -->
        <div v-if="loading" class="lms_search_state lms_search_loading">
          <i class="mdi mdi-loading lms_search_spinner"></i>
          <span>불러오는 중...</span>
        </div>
        <div v-else-if="filtered.length === 0" class="lms_search_state">
          {{ keyword ? "일치하는 항목이 없습니다." : "표시할 항목이 없습니다." }}
        </div>
        <div
          v-for="opt in filtered"
          v-else
          :key="String(getValue(opt))"
          class="lms_search_option"
          :class="{ selected: isSelected(opt), disabled: isMaxed && !isSelected(opt) }"
          @mousedown.prevent="toggleOption(opt)"
        >
          <div class="lms_search_option_body">
            <div class="lms_search_option_title">{{ getTitle(opt) }}</div>
            <!-- <div v-if="getSubtitle(opt) || getMeta(opt)" class="lms_search_option_meta">
              <span v-if="getSubtitle(opt)">{{ getSubtitle(opt) }}</span>
              <span v-if="getMeta(opt)">· {{ getMeta(opt) }}</span>
            </div> -->
          </div>

          <div class="lms_search_option_check">
            <i v-if="isSelected(opt)" class="icon_check"></i>
          </div>
        </div>
      </div>
    </Teleport>

    <div v-if="showChips && selectedItems.length > 0" class="lms_search_chips">
      <span v-for="item in selectedItems" :key="String(getValue(item))" class="lms_search_chip">
        <span class="lms_search_chip_label">
          {{ getTitle(item) }} 
          <span v-if="getSubtitle(item)" class="lms_search_chip_sub"
            >({{ getSubtitle(item) }})</span
          >
        </span>
        <button
          v-if="!disabled"
          type="button"
          class="lms_search_chip_remove"
          aria-label="제거"
          @click="removeByValue(getValue(item))"
        >
          <i class="mdi mdi-close"></i>
        </button>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";

const selectedValues = defineModel<(string | number)[]>({ default: () => [] });

const emit = defineEmits<{
  (e: "update:modelValue", value: (string | number)[]): void;
  (e: "change", selected: T[]): void;
  /** 입력 키워드 변경 — 서버사이드 검색 연동용(선택). 미사용 시 무시됨. */
  (e: "update:search", keyword: string): void;
}>();

const props = withDefaults(
  defineProps<{
    items: T[];
    itemValue?: string;
    itemTitle?: string;
    itemSubtitle?: string;
    itemMeta?: string;
    searchKeys?: string[];
    placeholder?: string;
    showChips?: boolean;
    disabled?: boolean;
    errorMessages?: string[];
    maxSelect?: number;
    single?: boolean; // 단일 선택 — 선택값을 박스 안에 표시(칩 없음), 새로 고르면 교체
    /** 서버사이드 검색 모드 — items 를 그대로 노출(클라 필터 생략, 깜빡임 방지) */
    serverSearch?: boolean;
    /** 검색 진행 중 — 빈 상태 대신 "검색 중..." 표시 */
    loading?: boolean;
  }>(),
  {
    itemValue: "userId",
    itemTitle: "korNm",
    itemSubtitle: "",
    itemMeta: "",
    searchKeys: () => ["korNm", "userId"],
    placeholder: "성명 / 학번 / 사번으로 검색",
    showChips: true,
    disabled: false,
    errorMessages: () => [],
    maxSelect: 0,
    single: false,
    serverSearch: false,
    loading: false,
  }
);

const isOpen = ref(false);
const keyword = ref("");
// 입력 키워드를 부모로 전달 — 서버사이드 검색 연동용 (기본 클라이언트 필터 동작엔 영향 없음)
watch(keyword, (v) => emit("update:search", v));
const inputEl = ref<HTMLInputElement | null>(null);
const dropdownRef = ref<HTMLDivElement | null>(null);
const dropdownStyle = ref<Record<string, string>>({});

const hasError = computed(() => props.errorMessages.length > 0);
const vals = computed(() => selectedValues.value ?? []);
const isMaxed = computed(() => props.maxSelect > 0 && vals.value.length >= props.maxSelect);

const getValue = (item: T) => item[props.itemValue];
const getTitle = (item: T) => (props.itemTitle ? item[props.itemTitle] : "") ?? "";
const getSubtitle = (item: T) => (props.itemSubtitle ? item[props.itemSubtitle] : "") ?? "";
const getMeta = (item: T) => (props.itemMeta ? item[props.itemMeta] : "") ?? "";

const selectedSet = computed(() => new Set(vals.value));
const isSelected = (item: T) => selectedSet.value.has(getValue(item));

const selectedItems = computed<T[]>(() => {
  const items = props.items ?? [];
  const map = new Map(items.map((i) => [getValue(i), i]));
  return vals.value.map((v) => map.get(v)).filter((v): v is T => v !== undefined);
});

// 단일 선택 모드 — 박스에 표시할 선택값 라벨 ("이름 (부가)")
const selectedLabel = computed(() => {
  const first = selectedItems.value[0];
  if (!first) return "";
  const sub = getSubtitle(first);
  return sub ? `${getTitle(first)} (${sub})` : getTitle(first);
});

const filtered = computed<T[]>(() => {
  const list = props.items ?? [];
  // 서버사이드 검색 모드 — 부모가 이미 keyword 로 필터한 결과를 줌. 클라 필터 생략(깜빡임 방지)
  if (props.serverSearch) return list;
  const q = keyword.value.trim().toLowerCase();
  if (!q) return list;
  return list.filter((item) => {
    return props.searchKeys.some((key) => {
      const v = item[key];
      return v != null && String(v).toLowerCase().includes(q);
    });
  });
});

const positionDropdown = async () => {
  if (!inputEl.value) return;
  const wrap = inputEl.value.closest(".lms_search_input_wrap") as HTMLElement | null;
  if (!wrap) return;
  const rect = wrap.getBoundingClientRect();
  dropdownStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: "10100",
  };
  await nextTick();
  if (dropdownRef.value) {
    const ddRect = dropdownRef.value.getBoundingClientRect();
    if (ddRect.bottom > window.innerHeight) {
      dropdownStyle.value = {
        position: "fixed",
        bottom: `${window.innerHeight - rect.top + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: "10100",
      };
    }
  }
};

const openDropdown = async () => {
  if (props.disabled) return;
  if (isOpen.value) return;
  isOpen.value = true;
  await nextTick();
  await positionDropdown();
  document.addEventListener("mousedown", onDocumentMouseDown, true);
};

const closeDropdown = () => {
  isOpen.value = false;
  document.removeEventListener("mousedown", onDocumentMouseDown, true);
};

const focusInput = () => {
  if (props.disabled) return;
  inputEl.value?.focus();
};

const onDocumentMouseDown = (e: MouseEvent) => {
  const target = e.target as Node | null;
  if (!target) return;
  const wrap = inputEl.value?.closest(".lms_search_input_wrap");
  if (wrap?.contains(target)) return;
  if (dropdownRef.value?.contains(target)) return;
  closeDropdown();
};

const toggleOption = (item: T) => {
  const val = getValue(item);
  // 단일 선택: 선택 교체 후 닫기 (박스에 표시)
  if (props.single) {
    const next = isSelected(item) ? [] : [val];
    selectedValues.value = next;
    emit("update:modelValue", next);
    emit("change", next.length ? [item] : []);
    keyword.value = "";
    closeDropdown();
    return;
  }
  const next = [...vals.value];
  const idx = next.indexOf(val);
  if (idx >= 0) {
    next.splice(idx, 1);
  } else {
    if (isMaxed.value) return;
    next.push(val);
  }
  selectedValues.value = next;
  emit("update:modelValue", next);
  emit(
    "change",
    next.map((v) => (props.items ?? []).find((i) => getValue(i) === v)).filter((v): v is T => !!v)
  );
};

// 단일 모드에서 박스(선택값 표시) 클릭 시 드롭다운 열고 검색 input 포커스
const onWrapClick = () => {
  if (props.disabled) return;
  if (!isOpen.value) openDropdown();
  nextTick(() => inputEl.value?.focus());
};

const removeByValue = (val: string | number) => {
  const next = vals.value.filter((v) => v !== val);
  selectedValues.value = next;
  emit("update:modelValue", next);
  emit(
    "change",
    next.map((v) => (props.items ?? []).find((i) => getValue(i) === v)).filter((v): v is T => !!v)
  );
};

const onEnter = () => {
  if (filtered.value.length === 1) {
    toggleOption(filtered.value[0]);
  }
};

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocumentMouseDown, true);
});

defineExpose({ open: openDropdown, close: closeDropdown, clear: () => (keyword.value = "") });
</script>

<style scoped>
.lms_search_select {
  width: 100%;
}

.lms_search_input_wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  height: 40px;
  border: 1px solid var(--lms-outline-gray-main, #e0e0e0);
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.2s;
  box-sizing: border-box;
  cursor: text;
}

.lms_search_input_wrap.open {
  border-color: var(--lms-primary-400, #5c74f6);
  border-width: 2px;
  padding: 0 9px;
}

.lms_search_input_wrap.disabled {
  background-color: var(--lms-grey-100, #f6f7f7);
  cursor: not-allowed;
}

.lms_search_input_wrap.error {
  border-color: var(--lms-outline-error, #ff1a2d);
}

.lms_search_icon {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.35);
  flex-shrink: 0;
}

.lms_search_input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: "Pretendard", sans-serif;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.8);
  height: 100%;
}

/* 단일 선택 모드 — 박스에 표시되는 선택값 */
.lms_search_single_value {
  flex: 1;
  font-family: "Pretendard", sans-serif;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.lms_search_chips {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.lms_search_chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 10px;
  background: #eef1ff;
  color: #3b4ed0;
  border-radius: 16px;
  font-size: 13px;
  font-family: "Pretendard", sans-serif;
}

.lms_search_chip_sub {
  color: rgba(0, 0, 0, 0.5);
  margin-left: 2px;
  font-size: 12px;
}

.lms_search_chip_remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  color: #3b4ed0;
  cursor: pointer;
  border-radius: 50%;
}

.lms_search_chip_remove:hover {
  background: rgba(59, 78, 208, 0.15);
}

.lms_search_chip_remove .mdi {
  font-size: 12px;
}
</style>

<style>
.lms_search_dropdown {
  background: #fff;
  border-radius: 4px;
  box-shadow:
    0 1px 3px 0 rgba(145, 158, 171, 0.12),
    0 1px 1px 0 rgba(145, 157, 171, 0.14),
    0 2px 1px -1px rgba(145, 158, 171, 0.2);
  padding: 6px 0;
  max-height: 320px;
  overflow-y: auto;
}

.lms_search_state {
  padding: 14px 16px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
  text-align: center;
}

/* 로딩 인디케이터 — 받는 사람 목록(.msg_write_receiver_loading) 과 같은 스피너 톤 */
.lms_search_loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 96px;
  color: #9ca3af;
}

.lms_search_loading .lms_search_spinner {
  font-size: 24px;
  animation: lms_search_spin 0.9s linear infinite;
}

@keyframes lms_search_spin {
  to {
    transform: rotate(360deg);
  }
}

.lms_search_option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 14px;
  font-family: "Pretendard", sans-serif;
  cursor: pointer;
  transition: background-color 0.15s;
}

.lms_search_option:hover {
  background: #f6f7f7;
}

.lms_search_option.selected {
  background: rgba(0, 0, 0, 0.04);
}

.lms_search_option.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.lms_search_option_check {
  width: 14px;
  height: 14px;
  margin-top: 3px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lms_search_option_check .icon_check {
  font-size: 14px;
  background-color: #5c74f6 !important;
}

.lms_search_option_body {
  flex: 1;
  min-width: 0;
}

.lms_search_option_title {
  font-size: 16px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}

.lms_search_option.selected .lms_search_option_title {
  color: #5c74f6;
  font-weight: 600;
}

.lms_search_option_meta {
  margin-top: 2px;
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}
</style>
